/**
 * Cookie consent store - the single bridge between CookieFirst and the frontend.
 *
 * Everything consent-gated in the templates talks to `$store.consent`:
 * YouTube/Vimeo (Media/Rendering/Video.html), the iFrame content element
 * (IFrame/IFrame-1.html) and the Instagram element (Instagram/Default.html).
 *
 * Why this module exists: the store used to keep consent in a plain in-memory
 * array. `allow()` pushed a string into that array and nothing else, so the
 * grant was forgotten on the next page load and every embed had to be unlocked
 * again, on every page, forever. Consent has to live in CookieFirst - that is
 * where it is persisted (cookie `cookiefirst-consent`) and where the visitor can
 * revoke it.
 *
 * Verified against CookieFirst on ihm.de (05.09.2026):
 *  - categories are exactly: necessary | performance | functional | advertising
 *  - `CookieFirst.acceptCategory(cat)` persists to the `cookiefirst-consent`
 *    cookie and survives a reload
 *  - changing consent triggers a FULL PAGE RELOAD, and no `cf_consent` /
 *    `cf_init` / `CookieFirstConsentChanged` event is ever dispatched on this
 *    setup - polling `window.CookieFirst.consent` is what actually works. The
 *    event listeners below are kept as a harmless fallback for CookieFirst
 *    configurations that do emit them.
 */

/** The four categories CookieFirst itself knows about. */
export const COOKIEFIRST_CATEGORIES = ['necessary', 'performance', 'functional', 'advertising'];

/**
 * Additional names templates may use for a category. Editors type these into the
 * "required cookies" field, and the existing templates already ask for
 * ['analytics','tracking']. Purely additive - a name here resolves to a category,
 * and every granted category publishes its aliases into `list`.
 */
export const CATEGORY_ALIASES = {
    necessary: ['essential'],
    performance: ['analytics', 'statistics'],
    functional: ['preferences'],
    advertising: ['tracking', 'marketing'],
};

const SCROLL_KEY = 'fullhd:consentScrollRestore';
const SCROLL_MAX_AGE = 15000;

/** Map any name (category or alias) onto its CookieFirst category. */
export function resolveCategory(name) {
    const needle = String(name || '').trim().toLowerCase();
    if (!needle) {
        return null;
    }

    if (COOKIEFIRST_CATEGORIES.includes(needle)) {
        return needle;
    }

    return Object.keys(CATEGORY_ALIASES)
        .find((category) => CATEGORY_ALIASES[category].includes(needle)) || null;
}

/** Every name a granted category should answer to. */
function expand(category) {
    return [category, ...(CATEGORY_ALIASES[category] || [])];
}

/**
 * CookieFirst reloads the page when consent changes, which would otherwise drop
 * the visitor back at the top - away from the embed they just unlocked.
 */
function rememberScrollPosition() {
    try {
        sessionStorage.setItem(SCROLL_KEY, JSON.stringify({
            path: window.location.pathname,
            y: window.scrollY,
            at: Date.now(),
        }));
    } catch (error) {
        // Private mode / storage disabled - losing the scroll position is not fatal.
    }
}

function restoreScrollPosition() {
    let saved;
    try {
        saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
        sessionStorage.removeItem(SCROLL_KEY);
    } catch (error) {
        return;
    }

    if (!saved || saved.path !== window.location.pathname) {
        return;
    }
    if (Date.now() - saved.at > SCROLL_MAX_AGE || !saved.y) {
        return;
    }

    window.requestAnimationFrame(() => window.scrollTo(0, saved.y));
}

export function registerConsentStore(Alpine) {
    Alpine.store('consent', {
        /** Flat list of granted category names plus their aliases. */
        list: [],
        /** True once CookieFirst has answered (or we gave up waiting on it). */
        ready: false,
        /** Names granted by an explicit click this page load, kept across polls. */
        pending: [],

        has(requirements = []) {
            const list = this.list;
            return requirements.every((requirement) => list.includes(requirement));
        },

        /**
         * Grant consent for the given names. Writes through to CookieFirst so the
         * choice is remembered on the next page load; falls back to a local grant
         * when CookieFirst is unavailable (backend preview, local dev).
         */
        allow(consents = []) {
            const names = (Array.isArray(consents) ? consents : [consents])
                .map((name) => String(name || '').trim())
                .filter(Boolean);

            if (!names.length) {
                return;
            }

            const categories = [];
            const unresolved = [];
            names.forEach((name) => {
                const category = resolveCategory(name);
                if (!category) {
                    unresolved.push(name);
                } else if (!categories.includes(category)) {
                    categories.push(category);
                }
            });

            // Show the change immediately; a reload (if CookieFirst does one) will
            // confirm it from the cookie anyway.
            const optimistic = new Set([...this.list, ...names]);
            categories.forEach((category) => expand(category).forEach((alias) => optimistic.add(alias)));
            this.list = Array.from(optimistic);
            this.pending = Array.from(new Set([...this.pending, ...names]));

            const cookieFirst = window.CookieFirst;
            if (!cookieFirst) {
                console.warn('[Consent] CookieFirst not available - granting locally only, this will not be remembered');
                return;
            }

            if (unresolved.length) {
                console.warn('[Consent] no CookieFirst category for', unresolved, '- opening the consent panel instead');
                if (typeof cookieFirst.openPanel === 'function') {
                    cookieFirst.openPanel();
                }
            }

            if (!categories.length) {
                return;
            }

            rememberScrollPosition();

            if (typeof cookieFirst.acceptCategory === 'function') {
                categories.forEach((category) => cookieFirst.acceptCategory(category));
                return;
            }

            if (typeof cookieFirst.updateConsent === 'function') {
                const payload = { ...(cookieFirst.consent || {}) };
                categories.forEach((category) => { payload[category] = true; });
                cookieFirst.updateConsent(payload);
                return;
            }

            console.warn('[Consent] CookieFirst offers no way to grant a category - opening the panel');
            if (typeof cookieFirst.openPanel === 'function') {
                cookieFirst.openPanel();
            }
        },

        /** Rebuild `list` from a CookieFirst consent object. */
        syncFromCookieFirst(consent) {
            if (!consent || typeof consent !== 'object') {
                return;
            }

            const granted = new Set(this.pending);
            Object.keys(consent).forEach((key) => {
                if (!consent[key]) {
                    return;
                }
                const category = resolveCategory(key) || key;
                expand(category).forEach((alias) => granted.add(alias));
            });

            const next = Array.from(granted);
            if (next.length !== this.list.length || next.some((name) => !this.list.includes(name))) {
                this.list = next;
            }
            this.ready = true;
        },
    });

    startCookieFirstSync(Alpine.store('consent'));
}

function startCookieFirstSync(store) {
    restoreScrollPosition();

    const sync = (consent) => store.syncFromCookieFirst(
        consent && consent.consent ? consent.consent : consent
    );

    const fromCookieFirst = () => {
        if (window.CookieFirst && window.CookieFirst.consent) {
            sync(window.CookieFirst.consent);
            return true;
        }
        return false;
    };

    if (!fromCookieFirst()) {
        // CookieFirst loads async and dispatches nothing on this setup, so poll it.
        let attempts = 0;
        const timer = window.setInterval(() => {
            attempts += 1;
            if (fromCookieFirst() || attempts >= 20) {
                window.clearInterval(timer);
                if (attempts >= 20 && !store.ready) {
                    store.ready = true;
                }
            }
        }, 250);
    }

    // Fallback for CookieFirst setups that do emit events.
    ['cf_consent', 'cf_init', 'cookiefirst-consent-update', 'CookieFirstConsentChanged']
        .forEach((name) => window.addEventListener(name, (event) => {
            sync(event.detail || (window.CookieFirst && window.CookieFirst.consent));
        }));
}
